import {
  aggregateMaterialsStorage,
  formatStorageBytes,
  storageBarDisplaySegments,
  type MaterialsStorageStats,
} from "@/lib/materials-storage";
import type { MaterialsItem } from "@/lib/materials-api";
import { cn } from "@/lib/utils";

type Props = {
  /** 当前工作区已加载的素材（与列表同源，非单文件夹切片） */
  items: MaterialsItem[];
  className?: string;
};

function StorageBarVisual({ stats }: { stats: MaterialsStorageStats }) {
  const { usedSegments, freePercent } = storageBarDisplaySegments(stats);

  return (
    <div
      className="flex h-2 w-full min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={`已用 ${formatStorageBytes(stats.usedBytes)} / ${formatStorageBytes(stats.quotaBytes)}`}
    >
      {usedSegments.map((seg, index) => (
        <div
          key={seg.id}
          className={cn(
            "h-full shrink-0",
            // 近空时 CSS 再保一层可见色块（约 3px），与百分比抬升互补
            index === 0 && stats.usedBytes > 0 && "min-w-[3px]",
            seg.colorClass,
          )}
          style={{ width: `${seg.displayPercent}%` }}
          title={`${seg.label} ${formatStorageBytes(seg.bytes)}`}
        />
      ))}
      {freePercent > 0 ? (
        <div
          className="h-full min-w-0 flex-1 bg-muted"
          style={{ width: `${freePercent}%` }}
          title={`可用 ${formatStorageBytes(stats.freeBytes)}`}
        />
      ) : null}
    </div>
  );
}

/** 有用量类型的色块 + 名称 + 文件数 + 字节；无用量时不展示该行项 */
function StorageTypeLegend({ stats }: { stats: MaterialsStorageStats }) {
  const used = stats.segments.filter((s) => s.count > 0 || s.bytes > 0);
  if (used.length === 0) return null;

  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] leading-4 text-muted-foreground">
      {used.map((seg) => (
        <li key={seg.id} className="inline-flex items-center gap-1.5">
          <span
            className={cn("size-2.5 shrink-0 rounded-[2px]", seg.colorClass)}
            aria-hidden
          />
          <span>{seg.label}</span>
          <span className="tabular-nums">
            {seg.count} 个 · {formatStorageBytes(seg.bytes)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** 素材库页脚：紧凑容量条 + 下方类型图例（占位配额 1 GB） */
export function MaterialsStorageBar({ items, className }: Props) {
  const stats = aggregateMaterialsStorage(items);

  return (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-1 bg-muted/20 px-2 py-1",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <StorageBarVisual stats={stats} />
        <p className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            已用 {formatStorageBytes(stats.usedBytes)}
          </span>
          <span> / {formatStorageBytes(stats.quotaBytes)}</span>
          {stats.itemCount > 0 ? (
            <span> · {stats.itemCount} 个文件</span>
          ) : null}
        </p>
      </div>
      <StorageTypeLegend stats={stats} />
    </div>
  );
}
